import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  BedDouble,
  CalendarClock,
  ClipboardList,
  FileText,
  History,
  Plus,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppointmentArrivalsModal } from './AppointmentArrivalsModal';
import { HospitalizationLookupModal } from './HospitalizationLookupModal';
import { OpeningAndCorrectionsModal } from './OpeningAndCorrectionsModal';
import { ShiftHistoryModal } from './ShiftHistoryModal';

import {
  MY_ACTIVE_CASH_SHIFT_QUERY,
  MY_CASH_SHIFT_STATUS_QUERY,
} from '@/modules/cash-shifts/cash-shifts.gql';
import { ClinicalVisitModal } from '@/modules/clinical-records/ClinicalVisitModal';
import { CustomerPetPicker } from '@/modules/doctor-pos/CustomerPetPicker';
import {
  ADD_DOCTOR_SALE_LINE_MUTATION,
  REMOVE_DOCTOR_SALE_LINE_MUTATION,
  UPDATE_DOCTOR_SALE_LINE_MUTATION,
} from '@/modules/doctor-pos/doctor-pos.gql';
import { OrderLinesPanel } from '@/modules/doctor-pos/OrderLinesPanel';
import { SellableSearchPanel, type SellableHit } from '@/modules/doctor-pos/SellableSearchPanel';
import { RequestCfdiDialog } from '@/modules/invoicing/RequestCfdiDialog';
import {
  ADD_CASHIER_SALE_LINE_MUTATION,
  APPLY_CASHIER_ORDER_DISCOUNT_MUTATION,
  CASHIER_CONSULTING_ROOMS_QUERY,
  CASHIER_SALE_ORDER_QUERY,
  CHARGE_SINGLE_STATION_ORDER_MUTATION,
  CREATE_CASHIER_MEDICAL_ORDER_MUTATION,
  DISCARD_CASHIER_OPENING_ORDER_MUTATION,
  KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
  KIT_BREAKDOWN_LINES_QUERY,
  REMOVE_CASHIER_SALE_LINE_MUTATION,
  START_SINGLE_STATION_ATTENTION_MUTATION,
  UPDATE_CASHIER_MEDICAL_ORDER_MUTATION,
  UPDATE_CASHIER_SALE_LINE_MUTATION,
} from '@/modules/cashier-pos/cashier-pos.gql';
import { PaymentPanel, type PaymentDraftValue } from '@/modules/cashier-pos/PaymentPanel';
import {
  applyApolloErrorMessage,
  collectKitBreakdownTargets,
  customerLabel,
  normalizeOptionalText,
  normalizeSaleOrder,
  parseWeightKgToGrams,
  petLabel,
  printSaleTicket,
  safeTicketBigInt,
  summarizePaymentMethodLines,
  ticketLabel,
  type SaleOrder,
  type SaleOrderItem,
} from '@/modules/cashier-pos/sale-order-shared';
import { OPEN_ACCOUNT_KIND_TO_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useActiveShiftStore } from '@/stores/activeShiftStore';
import { useAuthStore } from '@/stores/authStore';
import { useSingleStationPosStore } from '@/stores/singleStationPosStore';
import { useWorkstationStore } from '@/stores/workstationStore';

interface ConsultingRoomLite {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  branch_id: string;
  workstation_id: string | null;
}

interface ConsultingRoomsResponse {
  consultingRooms: { items: ConsultingRoomLite[] };
}

interface CashierOrderResponse {
  cashierSaleOrder: SaleOrder;
}

interface MyActiveShiftResponse {
  myActiveCashShift: { id: string; cash_register_id: string; branch_id: string; opened_at: string } | null;
}

interface MyCashShiftStatusResponse {
  myCashShiftStatus: {
    has_own_open_shift: boolean;
    has_branch_open_shift: boolean;
    branch_open_shift_cashier_name: string | null;
  };
}

const WORKABLE_STATUSES = new Set([
  'created_by_cashier',
  'returned_to_cashier_for_correction',
  'in_medical_attention',
  'taken_by_doctor',
]);

// eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header name, not a JS identifier
function idempotencyContext(): { headers: { 'Idempotency-Key': string } } {
  const key =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header name, not a JS identifier
  return { headers: { 'Idempotency-Key': key } };
}

/**
 * Traditional single-window POS used only when `pos.single_station_mode_enabled` is on
 * (gated by `CashierPosGate`). Mirrors the sale-order lifecycle the two-station
 * `CashierPosPage` already implements for single-station users (opening → doctor-scoped
 * editing → charge, via `startSingleStationAttention`/`chargeSingleStationOrder`) but
 * without the "sent to doctor" / "pending for cashier" queues, which never apply here —
 * a single-station order transitions through those statuses only inside one backend
 * transaction and is never durably observable in them (see plan research). Secondary
 * tasks (citas, aperturas/correcciones, hospitalizaciones, historial del turno) live in
 * modals so the main window stays focused on the active sale.
 */
export function SingleStationPosPage(): JSX.Element {
  const { t } = useTranslation();
  const apollo = useApolloClient();

  const active_order_id = useSingleStationPosStore((s) => s.active_order_id);
  const setActiveOrder = useSingleStationPosStore((s) => s.setActiveOrder);
  const own_workstation_id = useWorkstationStore((s) => s.workstation_id);
  const cashier_name = useAuthStore((s) => s.user?.full_name ?? 'Caja');
  const user_perms = useAuthStore((s) => s.user?.permissions ?? []);

  const setActiveShift = useActiveShiftStore((s) => s.setActive);
  const clearActiveShift = useActiveShiftStore((s) => s.clear);
  const active_shift_id = useActiveShiftStore((s) => s.shift_id);

  const [error_text, setErrorText] = useState<string | null>(null);
  const [draft_customer_id, setDraftCustomerId] = useState<string | null>(null);
  const [draft_pet_id, setDraftPetId] = useState<string | null>(null);
  const [weight_kg_draft, setWeightKgDraft] = useState('');
  const [weight_locked, setWeightLocked] = useState(false);
  const [consulting_room_id_draft, setConsultingRoomIdDraft] = useState('');
  const [cashier_notes_draft, setCashierNotesDraft] = useState('');
  const [kit_breakdowns, setKitBreakdowns] = useState<Record<string, string[]>>({});
  const [last_paid_folio, setLastPaidFolio] = useState<string | null>(null);
  const [last_paid_sale, setLastPaidSale] = useState<{ id: string; customer_id: string } | null>(
    null,
  );
  const [show_clinical_modal, setShowClinicalModal] = useState(false);
  const [show_request_cfdi, setShowRequestCfdi] = useState(false);
  const [show_opening_modal, setShowOpeningModal] = useState(false);
  const [show_hospitalization_modal, setShowHospitalizationModal] = useState(false);
  const [show_appointments_modal, setShowAppointmentsModal] = useState(false);
  const [show_shift_history_modal, setShowShiftHistoryModal] = useState(false);

  const { data: shift_data } = useQuery<MyActiveShiftResponse>(MY_ACTIVE_CASH_SHIFT_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const { data: shift_status_data } = useQuery<MyCashShiftStatusResponse>(
    MY_CASH_SHIFT_STATUS_QUERY,
    { fetchPolicy: 'cache-and-network', pollInterval: 60_000 },
  );
  useEffect(() => {
    const s = shift_data?.myActiveCashShift;
    if (s) {
      setActiveShift({
        shift_id: s.id,
        cash_register_id: s.cash_register_id,
        branch_id: s.branch_id,
        opened_at: s.opened_at,
      });
    } else {
      clearActiveShift();
    }
  }, [shift_data, setActiveShift, clearActiveShift]);
  const has_open_shift = active_shift_id !== null;
  const has_branch_open_shift = shift_status_data?.myCashShiftStatus.has_branch_open_shift ?? false;
  const branch_open_shift_cashier_name =
    shift_status_data?.myCashShiftStatus.branch_open_shift_cashier_name ?? null;
  const no_shift_banner_text =
    !has_open_shift && has_branch_open_shift
      ? t('cashier_pos.no_open_shift_assigned_to_other_cashier_banner', {
          cashier: branch_open_shift_cashier_name ?? t('cashier_pos.other_cashier_fallback'),
        })
      : t('cashier_pos.no_open_shift_banner');

  const { data: rooms_data } = useQuery<ConsultingRoomsResponse>(CASHIER_CONSULTING_ROOMS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const own_workstation_room_id =
    (rooms_data?.consultingRooms.items ?? []).find(
      (room) => room.status === 'active' && room.workstation_id === own_workstation_id,
    )?.id ?? null;

  const { data: selected_data, refetch: refetchSelected } = useQuery<CashierOrderResponse>(
    CASHIER_SALE_ORDER_QUERY,
    {
      variables: { id: active_order_id ?? '' },
      skip: !active_order_id,
      fetchPolicy: 'cache-and-network',
    },
  );
  const selected: SaleOrder | null = selected_data?.cashierSaleOrder
    ? normalizeSaleOrder(selected_data.cashierSaleOrder)
    : null;

  // A stale/finished order (charged elsewhere, cancelled, etc.) is not workable here —
  // drop it so the window resets to an empty ticket instead of showing a dead order.
  useEffect(() => {
    if (selected && !WORKABLE_STATUSES.has(selected.status)) {
      setActiveOrder(null);
    }
  }, [selected, setActiveOrder]);

  const selected_is_opening =
    selected?.status === 'created_by_cashier' ||
    selected?.status === 'returned_to_cashier_for_correction';
  const selected_is_active =
    selected?.status === 'in_medical_attention' || selected?.status === 'taken_by_doctor';

  useEffect(() => {
    if (!active_order_id) {
      setDraftCustomerId(null);
      setDraftPetId(null);
      setWeightKgDraft('');
      setWeightLocked(false);
      setConsultingRoomIdDraft('');
      setCashierNotesDraft('');
    }
  }, [active_order_id]);

  useEffect(() => {
    if (!selected || !selected_is_opening) return;
    setDraftCustomerId(selected.customer_id);
    setDraftPetId(selected.pet_id);
    setCashierNotesDraft(selected.cashier_initial_notes ?? '');
    setWeightKgDraft(
      selected.current_weight_grams ? (selected.current_weight_grams / 1000).toFixed(3) : '',
    );
    setWeightLocked(Boolean(selected.current_weight_grams));
    setConsultingRoomIdDraft(selected.consulting_room_id ?? own_workstation_room_id ?? '');
  }, [
    selected?.id,
    selected?.customer_id,
    selected?.pet_id,
    selected?.cashier_initial_notes,
    selected?.current_weight_grams,
    selected?.consulting_room_id,
    selected_is_opening,
    own_workstation_room_id,
  ]);

  const [createCashierMedicalOrder] = useMutation<{ createCashierMedicalOrder: SaleOrder }>(
    CREATE_CASHIER_MEDICAL_ORDER_MUTATION,
  );
  const [updateCashierMedicalOrder] = useMutation<{ updateCashierMedicalOrder: SaleOrder }>(
    UPDATE_CASHIER_MEDICAL_ORDER_MUTATION,
  );
  const [addCashierSaleLine, { loading: adding_opening_line }] = useMutation<{
    addCashierSaleLine: SaleOrder;
  }>(ADD_CASHIER_SALE_LINE_MUTATION);
  const [updateCashierSaleLine, { loading: updating_opening_line }] = useMutation<{
    updateCashierSaleLine: SaleOrder;
  }>(UPDATE_CASHIER_SALE_LINE_MUTATION);
  const [removeCashierSaleLine, { loading: removing_opening_line }] = useMutation<{
    removeCashierSaleLine: SaleOrder;
  }>(REMOVE_CASHIER_SALE_LINE_MUTATION);
  const [startSingleStationAttention, { loading: starting_attention }] = useMutation<{
    startSingleStationAttention: SaleOrder;
  }>(START_SINGLE_STATION_ATTENTION_MUTATION);
  const [addDoctorSaleLine, { loading: adding_active_line }] = useMutation<{
    addDoctorSaleLine: SaleOrder;
  }>(ADD_DOCTOR_SALE_LINE_MUTATION);
  const [updateDoctorSaleLine, { loading: updating_active_line }] = useMutation<{
    updateDoctorSaleLine: SaleOrder;
  }>(UPDATE_DOCTOR_SALE_LINE_MUTATION);
  const [removeDoctorSaleLine, { loading: removing_active_line }] = useMutation<{
    removeDoctorSaleLine: SaleOrder;
  }>(REMOVE_DOCTOR_SALE_LINE_MUTATION);
  const [applyCashierOrderDiscount, { loading: applying_discount }] = useMutation<{
    applyCashierOrderDiscount: SaleOrder;
  }>(APPLY_CASHIER_ORDER_DISCOUNT_MUTATION);
  const [chargeSingleStationOrder, { loading: charging }] = useMutation<{
    chargeSingleStationOrder: SaleOrder;
  }>(CHARGE_SINGLE_STATION_ORDER_MUTATION);
  const [discardCashierOpeningOrder, { loading: discarding }] = useMutation<{
    discardCashierOpeningOrder: SaleOrder;
  }>(DISCARD_CASHIER_OPENING_ORDER_MUTATION);

  const opening_busy = adding_opening_line || updating_opening_line || removing_opening_line;
  const active_line_busy = adding_active_line || updating_active_line || removing_active_line;

  const ensureOrder = useCallback(async (): Promise<string> => {
    if (active_order_id) return active_order_id;
    const result = await createCashierMedicalOrder({
      variables: { input: {} },
      context: idempotencyContext(),
    });
    const created_id = result.data?.createCashierMedicalOrder.id;
    if (!created_id) throw new Error('create_order_failed');
    setActiveOrder(created_id);
    return created_id;
  }, [active_order_id, createCashierMedicalOrder, setActiveOrder]);

  const onChangeCustomerPet = useCallback(
    async (next: { customer_id: string | null; pet_id: string | null }): Promise<void> => {
      setErrorText(null);
      setDraftCustomerId(next.customer_id);
      setDraftPetId(next.pet_id);
      try {
        const id = await ensureOrder();
        await updateCashierMedicalOrder({
          variables: {
            input: { sale_order_id: id, customer_id: next.customer_id, pet_id: next.pet_id },
          },
          context: idempotencyContext(),
        });
        await refetchSelected({ id });
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [ensureOrder, updateCashierMedicalOrder, refetchSelected, t],
  );

  const syncOpeningMeta = useCallback(
    async (order_id: string, options?: { force?: boolean }): Promise<void> => {
      const weight_grams = parseWeightKgToGrams(weight_kg_draft);
      const next_room_id = consulting_room_id_draft || null;
      const next_notes = normalizeOptionalText(cashier_notes_draft);
      const current_notes = normalizeOptionalText(selected?.cashier_initial_notes ?? null);
      const needs_sync =
        (selected?.consulting_room_id ?? null) !== next_room_id ||
        current_notes !== next_notes ||
        (selected?.current_weight_grams ?? null) !== (weight_grams ?? null);
      if (!options?.force && !needs_sync) return;
      await updateCashierMedicalOrder({
        variables: {
          input: {
            sale_order_id: order_id,
            consulting_room_id: next_room_id,
            cashier_initial_notes: cashier_notes_draft,
            current_weight_grams: weight_grams,
          },
        },
        context: idempotencyContext(),
      });
    },
    [
      weight_kg_draft,
      consulting_room_id_draft,
      cashier_notes_draft,
      selected?.consulting_room_id,
      selected?.cashier_initial_notes,
      selected?.current_weight_grams,
      updateCashierMedicalOrder,
    ],
  );

  const onWeightBlurSave = useCallback(async (): Promise<void> => {
    if (!weight_kg_draft.trim() || weight_locked) return;
    setErrorText(null);
    try {
      const id = await ensureOrder();
      await syncOpeningMeta(id, { force: true });
      await refetchSelected({ id });
      setWeightLocked(true);
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [weight_kg_draft, weight_locked, ensureOrder, syncOpeningMeta, refetchSelected, t]);

  const onAddLine = useCallback(
    async (hit: SellableHit, quantity: string): Promise<void> => {
      setErrorText(null);
      try {
        if (selected_is_active && selected) {
          await addDoctorSaleLine({
            variables: {
              input: {
                sale_order_id: selected.id,
                item_kind: hit.kind.toUpperCase(),
                product_id: hit.kind === 'product' ? hit.id : null,
                service_id: hit.kind === 'service' ? hit.id : null,
                kit_id: hit.kind === 'kit' ? hit.id : null,
                quantity,
              },
            },
          });
          await refetchSelected();
          return;
        }
        const id = await ensureOrder();
        await syncOpeningMeta(id);
        await addCashierSaleLine({
          variables: {
            input: {
              sale_order_id: id,
              item_kind: hit.kind.toUpperCase(),
              product_id: hit.kind === 'product' ? hit.id : null,
              service_id: hit.kind === 'service' ? hit.id : null,
              kit_id: hit.kind === 'kit' ? hit.id : null,
              quantity,
            },
          },
        });
        await refetchSelected({ id });
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [
      selected,
      selected_is_active,
      addDoctorSaleLine,
      ensureOrder,
      syncOpeningMeta,
      addCashierSaleLine,
      refetchSelected,
      t,
    ],
  );

  const onUpdateLineQty = useCallback(
    async (item_id: string, quantity: string): Promise<void> => {
      setErrorText(null);
      try {
        if (selected_is_active) {
          await updateDoctorSaleLine({ variables: { input: { sale_order_item_id: item_id, quantity } } });
        } else {
          await updateCashierSaleLine({
            variables: { input: { sale_order_item_id: item_id, quantity } },
          });
        }
        await refetchSelected();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected_is_active, updateDoctorSaleLine, updateCashierSaleLine, refetchSelected, t],
  );

  const onRemoveLine = useCallback(
    async (item_id: string): Promise<void> => {
      setErrorText(null);
      try {
        if (selected_is_active) {
          await removeDoctorSaleLine({ variables: { sale_order_item_id: item_id } });
        } else {
          await removeCashierSaleLine({ variables: { sale_order_item_id: item_id } });
        }
        await refetchSelected();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected_is_active, removeDoctorSaleLine, removeCashierSaleLine, refetchSelected, t],
  );

  const onStartAttention = useCallback(async (): Promise<void> => {
    if (!selected || !selected_is_opening) return;
    setErrorText(null);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' must also fall through, not just null/undefined
    const effective_room_id = consulting_room_id_draft || selected.consulting_room_id || own_workstation_room_id;
    const effective_weight_grams = parseWeightKgToGrams(weight_kg_draft);
    if (!selected.customer_id) return setErrorText(t('doctor_pos.customer_required'));
    if (!selected.pet_id) return setErrorText(t('doctor_pos.pet_required'));
    if (!effective_weight_grams || effective_weight_grams <= 0)
      return setErrorText(t('doctor_pos.weight_missing'));
    if (!effective_room_id) return setErrorText(t('cashier_pos.select_consulting_room'));
    if (selected.items.length === 0) return setErrorText(t('doctor_pos.lines_required'));
    try {
      await syncOpeningMeta(selected.id, { force: true });
      await startSingleStationAttention({
        variables: { input: { sale_order_id: selected.id } },
        context: idempotencyContext(),
      });
      await refetchSelected({ id: selected.id });
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [
    selected,
    selected_is_opening,
    consulting_room_id_draft,
    own_workstation_room_id,
    weight_kg_draft,
    syncOpeningMeta,
    startSingleStationAttention,
    refetchSelected,
    t,
  ]);

  const loadMissingKitBreakdowns = useCallback(
    async (items: readonly SaleOrderItem[]): Promise<Record<string, string[]>> => {
      const targets = collectKitBreakdownTargets(items);
      const missing_targets = targets.filter(
        (target) => !Object.prototype.hasOwnProperty.call(kit_breakdowns, target.key),
      );
      if (missing_targets.length === 0) return kit_breakdowns;
      const fetched: Record<string, string[]> = {};
      for (const target of missing_targets) {
        try {
          if (target.kind === 'id') {
            const res = await apollo.query<{ kitBreakdownLines: string[] }>({
              query: KIT_BREAKDOWN_LINES_QUERY,
              variables: { kit_id: target.value },
              fetchPolicy: 'network-only',
            });
            fetched[target.key] = res.data?.kitBreakdownLines ?? [];
            continue;
          }
          const res = await apollo.query<{ kitBreakdownLinesByCode: string[] }>({
            query: KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
            variables: { kit_code: target.value },
            fetchPolicy: 'network-only',
          });
          fetched[target.key] = res.data?.kitBreakdownLinesByCode ?? [];
        } catch {
          fetched[target.key] = [];
        }
      }
      const merged = { ...kit_breakdowns, ...fetched };
      setKitBreakdowns(merged);
      return merged;
    },
    [apollo, kit_breakdowns],
  );

  const onCharge = useCallback(
    async (
      drafts: PaymentDraftValue[],
      options: { open_account_kind: 'normal' | 'hospitalization_1' | 'hospitalization_2' | null },
    ): Promise<void> => {
      if (!selected) return;
      setErrorText(null);
      const ticket_window =
        typeof window !== 'undefined' ? window.open('', '_blank', 'width=420,height=720') : null;
      try {
        const charge_input = {
          sale_order_id: selected.id,
          payments: drafts.map((d) => {
            const trimmed_ref = d.reference_number?.trim();
            return {
              payment_method_id: d.payment_method_id,
              amount_cents: d.amount_cents,
              reference_number: trimmed_ref && trimmed_ref.length > 0 ? trimmed_ref : null,
              allow_change: d.allow_change,
            };
          }),
          open_account_kind: options.open_account_kind
            ? OPEN_ACCOUNT_KIND_TO_GQL[options.open_account_kind]
            : null,
          auto_create_open_account: options.open_account_kind !== null,
        };
        const result = await chargeSingleStationOrder({
          variables: { input: charge_input },
          context: idempotencyContext(),
        });
        const charged = result.data?.chargeSingleStationOrder;
        const charged_order = charged ? normalizeSaleOrder(charged) : selected;
        setActiveOrder(null);
        setErrorText(null);
        setLastPaidFolio(charged_order.folio_number ?? selected.id.slice(0, 8));
        setLastPaidSale(
          selected.customer_id ? { id: selected.id, customer_id: selected.customer_id } : null,
        );
        window.setTimeout(() => {
          const submitted_total_cents = drafts.reduce<bigint>(
            (acc, draft) => acc + safeTicketBigInt(draft.amount_cents),
            0n,
          );
          const ticket_total_cents = safeTicketBigInt(charged_order.total_cents);
          const open_account_pending_cents =
            options.open_account_kind !== null && submitted_total_cents < ticket_total_cents
              ? (ticket_total_cents - submitted_total_cents).toString()
              : null;
          const open_account_credit_cents =
            options.open_account_kind === 'hospitalization_1' &&
            submitted_total_cents > ticket_total_cents
              ? (submitted_total_cents - ticket_total_cents).toString()
              : null;
          void loadMissingKitBreakdowns(charged_order.items).then((resolved_breakdowns) =>
            printSaleTicket(
              charged_order,
              {
                mode: 'ticket',
                cashier_name,
                ticket_number: ticketLabel(null, charged_order.folio_number),
                kit_breakdowns: resolved_breakdowns,
                payment_methods: drafts.map((draft) => ({
                  payment_method_id: draft.payment_method_id,
                  payment_method_code: draft.method_code,
                  payment_method_name: draft.method_name,
                  amount_cents: draft.amount_cents,
                  reference_number: draft.reference_number,
                })),
                open_account_kind: options.open_account_kind,
                open_account_pending_cents,
                open_account_credit_cents,
                payment_method_summary: summarizePaymentMethodLines(drafts),
              },
              ticket_window,
            ),
          );
        }, 0);
      } catch (err) {
        ticket_window?.close();
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected, chargeSingleStationOrder, setActiveOrder, cashier_name, loadMissingKitBreakdowns, t],
  );

  const onNewSale = useCallback(async (): Promise<void> => {
    if (selected_is_active) return; // an attention already in progress can only be closed by charging it
    setErrorText(null);
    try {
      if (selected && selected_is_opening) {
        await discardCashierOpeningOrder({
          variables: { input: { sale_order_id: selected.id } },
          context: idempotencyContext(),
        });
      }
      setActiveOrder(null);
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [selected, selected_is_opening, selected_is_active, discardCashierOpeningOrder, setActiveOrder, t]);

  const line_items = useMemo(
    () =>
      (selected?.items ?? []).map((it) => ({
        ...it,
        item_kind: it.item_kind as 'product' | 'service' | 'kit' | 'exception',
      })),
    [selected?.items],
  );

  const can_start_attention =
    Boolean(selected?.customer_id) &&
    Boolean(selected?.pet_id) &&
    weight_kg_draft.trim().length > 0 &&
    Boolean(consulting_room_id_draft || own_workstation_room_id) &&
    (selected?.items.length ?? 0) > 0;

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">
            {t('single_station_pos.title')}
          </h1>
          <p className="text-muted-foreground text-xs">
            {selected
              ? `${t('single_station_pos.folio_label')}: ${selected.folio_number ?? selected.id.slice(0, 8)}`
              : t('single_station_pos.no_active_order')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAppointmentsModal(true)}
          >
            <CalendarClock className="h-4 w-4" aria-hidden />
            <span>{t('single_station_pos.appointments_button')}</span>
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowOpeningModal(true)}>
            <ClipboardList className="h-4 w-4" aria-hidden />
            <span>{t('single_station_pos.opening_button')}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowHospitalizationModal(true)}
          >
            <BedDouble className="h-4 w-4" aria-hidden />
            <span>{t('single_station_pos.hospitalization_button')}</span>
          </Button>
          {selected?.clinical_visit_id ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowClinicalModal(true)}>
              <FileText className="h-4 w-4" aria-hidden />
              <span>{t('single_station_pos.clinical_record_button')}</span>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowShiftHistoryModal(true)}
          >
            <History className="h-4 w-4" aria-hidden />
            <span>{t('single_station_pos.shift_history_button')}</span>
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void onNewSale()}
            disabled={selected_is_active || discarding}
            title={selected_is_active ? t('single_station_pos.new_sale_disabled_hint') : undefined}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('single_station_pos.new_sale_button')}</span>
          </Button>
        </div>
      </header>

      {!has_open_shift ? (
        <div className="border-warning bg-warning/10 text-warning-foreground rounded-md border px-4 py-2 text-sm">
          {no_shift_banner_text}
        </div>
      ) : null}
      {error_text ? (
        <div className="border-destructive bg-destructive/10 text-destructive rounded-md border px-4 py-2 text-sm">
          {error_text}
        </div>
      ) : null}
      {last_paid_folio ? (
        <div className="border-success bg-success/10 text-success flex flex-wrap items-center justify-between gap-2 rounded-md border px-4 py-2 text-sm">
          <span>{t('cashier_pos.charged_banner', { folio: last_paid_folio })}</span>
          {last_paid_sale && user_perms.includes('invoicing.cfdi.request') ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setShowRequestCfdi(true)}>
              {t('invoicing.cta_request')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 pt-4">
              {selected_is_active ? (
                <div>
                  <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                    {selected?.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                  </p>
                  <p className="font-display text-lg font-bold tracking-tight">
                    {customerLabel(selected?.customer_snapshot_json)} -{' '}
                    {petLabel(selected?.pet_snapshot_json)}
                  </p>
                </div>
              ) : (
                <CustomerPetPicker
                  customer_id={draft_customer_id}
                  pet_id={draft_pet_id}
                  onChange={(next) => void onChangeCustomerPet(next)}
                  disabled={discarding}
                />
              )}
              {!selected_is_active ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('cashier_pos.current_weight_kg')}</Label>
                    <Input
                      value={weight_kg_draft}
                      onChange={(e) => setWeightKgDraft(e.target.value)}
                      onBlur={() => void onWeightBlurSave()}
                      placeholder="Ej. 4.250"
                      disabled={weight_locked}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('cashier_pos.consulting_room')}</Label>
                    <select
                      className="border-input bg-background h-11 w-full rounded-md border px-3 text-sm"
                      value={own_workstation_room_id ?? consulting_room_id_draft}
                      onChange={(e) => setConsultingRoomIdDraft(e.target.value)}
                      disabled={Boolean(own_workstation_room_id)}
                    >
                      <option value="">{t('cashier_pos.select_consulting_room')}</option>
                      {(rooms_data?.consultingRooms.items ?? []).map((room) => (
                        <option key={room.id} value={room.id}>
                          {room.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
              <SellableSearchPanel
                onAdd={onAddLine}
                disabled={opening_busy || active_line_busy}
                hide_internal_services
                preview_context={null}
                display_mode="grid"
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('single_station_pos.cart_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <OrderLinesPanel
                items={line_items}
                total_cents={selected?.total_cents ?? '0'}
                subtotal_cents={selected?.subtotal_cents ?? '0'}
                tax_cents={selected?.tax_cents ?? '0'}
                clinical_notes={cashier_notes_draft}
                is_editable={!selected_is_active}
                is_busy={opening_busy || active_line_busy}
                is_saving_notes={false}
                onUpdateQty={onUpdateLineQty}
                onRemove={onRemoveLine}
                onUpdateNotes={(notes) => setCashierNotesDraft(notes)}
              />
            </CardContent>
          </Card>

          {selected && selected_is_opening ? (
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => void onStartAttention()}
              disabled={!can_start_attention || starting_attention}
            >
              {t('cashier_pos.start_single_station_attention')}
            </Button>
          ) : null}

          {selected && selected_is_active ? (
            <Card>
              <CardContent className="pt-4">
                <PaymentPanel
                  order_id={selected.id}
                  total_cents={selected.total_cents}
                  subtotal_cents={selected.subtotal_cents}
                  discount_cents={selected.discount_cents}
                  tax_cents={selected.tax_cents}
                  charging={charging}
                  applying_discount={applying_discount}
                  on_charge={onCharge}
                  on_apply_discount={async (input) => {
                    setErrorText(null);
                    try {
                      await applyCashierOrderDiscount({
                        variables: {
                          input: {
                            sale_order_id: selected.id,
                            discount_cents: input.discount_cents,
                            otp_plaintext: input.otp_plaintext,
                            reason: input.reason,
                          },
                        },
                        context: idempotencyContext(),
                      });
                      await refetchSelected({ id: selected.id });
                    } catch (err) {
                      applyApolloErrorMessage(err, t, setErrorText);
                    }
                  }}
                  on_cancel={() => void onNewSale()}
                  disabled={!has_open_shift}
                  disabled_reason={!has_open_shift ? no_shift_banner_text : null}
                  open_account_available={Boolean(selected.customer_id && selected.pet_id)}
                />
              </CardContent>
            </Card>
          ) : null}

          {selected ? (
            <p className="text-muted-foreground text-right text-xs">
              {t('cashier_pos.total')}:{' '}
              <span className="font-mono font-semibold">
                {formatCentsString(selected.total_cents)}
              </span>
            </p>
          ) : null}
        </div>
      </div>

      {selected?.clinical_visit_id ? (
        <ClinicalVisitModal
          sale_order_id={selected.id}
          open={show_clinical_modal}
          onOpenChange={setShowClinicalModal}
        />
      ) : null}
      {show_request_cfdi && last_paid_sale ? (
        <RequestCfdiDialog
          sale_order_id={last_paid_sale.id}
          customer_id={last_paid_sale.customer_id}
          on_close={() => setShowRequestCfdi(false)}
          on_done={async () => setShowRequestCfdi(false)}
          on_error={setErrorText}
        />
      ) : null}
      {show_opening_modal ? (
        <OpeningAndCorrectionsModal
          on_close={() => setShowOpeningModal(false)}
          can_switch={!selected_is_active}
          on_select={(id) => {
            setActiveOrder(id);
            setShowOpeningModal(false);
          }}
          on_new={() => {
            setActiveOrder(null);
            setShowOpeningModal(false);
          }}
        />
      ) : null}
      {show_hospitalization_modal ? (
        <HospitalizationLookupModal on_close={() => setShowHospitalizationModal(false)} />
      ) : null}
      {show_appointments_modal ? (
        <AppointmentArrivalsModal
          on_close={() => setShowAppointmentsModal(false)}
          can_switch={!selected_is_active}
          on_select={(id) => {
            setActiveOrder(id);
            setShowAppointmentsModal(false);
          }}
        />
      ) : null}
      {show_shift_history_modal ? (
        <ShiftHistoryModal on_close={() => setShowShiftHistoryModal(false)} />
      ) : null}
    </div>
  );
}
